import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, Shield, Workflow, ArrowUpRight, Download } from "lucide-react";
import { EISG_IMG, PROGRAMMES, COHORTS } from "@/lib/data";
import { EISG } from "@/constants/testIds";
import CatalogueDialog from "@/components/eisg/CatalogueDialog";
import SectionHeader from "@/components/shared/SectionHeader";

const PILLARS_ICONS = [
  { icon: Brain, title: "Intelligence Artificielle", desc: "Comprendre, gouverner et déployer l'IA au niveau exécutif." },
  { icon: Shield, title: "Gouvernance", desc: "Cadres de gouvernance, risque, éthique et conformité institutionnelle." },
  { icon: Workflow, title: "Transformation Digitale", desc: "Piloter la transformation et créer de la valeur durable." },
];

const EISGHome = () => (
  <div>
    {/* HERO */}
    <section className="relative min-h-[95vh] flex items-center overflow-hidden bg-[#0A192F]">
      <div className="absolute inset-0">
        <img src={EISG_IMG.hero} alt="Johannesburg" className="w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#060D18]/95 via-[#0A192F]/85 to-[#0A192F]/40" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32 w-full text-white">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="accent-bar" />
              <span className="overline text-[#D4AF37] tracking-[0.32em] font-bold">EISG · Une division de BridgeDatum</span>
            </div>
            <h1 className="font-eisg text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Gouverner l'IA. <br /><span className="text-[#D4AF37] italic font-bold">Piloter</span> la transformation. <br />Créer de la valeur.
            </h1>
            <p className="mt-10 font-bd font-semibold text-xl sm:text-2xl lg:text-[26px] leading-snug text-white tracking-tight max-w-3xl">
              Institut Exécutif de Stratégie et de Gouvernance.
            </p>
            <p className="mt-6 text-base sm:text-lg text-white/75 leading-relaxed max-w-3xl font-body">
              EISG prépare les dirigeants africains, les conseils d'administration, les hauts cadres et les décideurs institutionnels à comprendre, gouverner et déployer l'intelligence artificielle et la transformation digitale avec impact.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/eisg/programmes" data-testid={EISG.heroCtaProgrammes} className="btn-premium btn-premium-gold">
                Découvrir les programmes <ArrowRight size={16} />
              </Link>
              <CatalogueDialog
                trigger={
                  <button data-testid={EISG.heroCtaCatalogue} className="btn-premium btn-premium-ghost">
                    <Download size={16} /> Télécharger le catalogue
                  </button>
                }
              />
              <Link to="/eisg/registration" data-testid={EISG.heroCtaPreinscription} className="btn-premium btn-premium-ghost">
                Pré-inscription <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          {/* Right floating card */}
          <div className="lg:col-span-4">
            <div className="bg-white/5 backdrop-blur-xl border border-white/15 p-8 space-y-6">
              <div>
                <div className="section-num text-[#D4AF37]">— 2026 / 2027</div>
                <h3 className="font-eisg text-2xl mt-2">Trois moments stratégiques</h3>
              </div>
              <ul className="space-y-4 text-sm">
                {COHORTS.map((c, i) => (
                  <li key={c.period} className="border-b border-white/10 pb-3 last:border-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">— 0{i + 1} · {c.location}</div>
                    <div className="font-bd font-semibold text-white mt-1">{c.programme}</div>
                    <div className="text-white/65 text-xs mt-0.5">{c.period}</div>
                  </li>
                ))}
              </ul>
              <Link to="/eisg/cohorts" className="inline-flex items-center gap-2 text-xs font-semibold text-[#D4AF37] uppercase tracking-wider hover:text-white">
                Calendrier complet <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* 3 PILLARS */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeader
          number="— 01"
          eyebrow="Trois disciplines exécutives"
          title="IA, Gouvernance, Transformation."
          subtitle="Un seul Institut. Une seule promesse exécutive."
          body="Trois disciplines stratégiques au cœur de chaque cohorte EISG."
          titleFont="eisg"
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0A192F]/10">
          {PILLARS_ICONS.map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="bg-white p-10 lg:p-12 lift-card">
              <div className="section-num">— 0{i + 1}</div>
              <Icon size={32} className="text-[#0A192F] mt-6 mb-6" />
              <h3 className="font-eisg text-2xl text-[#0A192F] tracking-tight">{title}</h3>
              <p className="mt-3 text-[#0A192F]/70 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* QUOTE */}
    <section className="py-28 lg:py-36 bg-[#0A192F] text-white grain">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="overline text-[#D4AF37] mb-8">— Manifeste EISG</div>
        <p className="font-eisg text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] max-w-5xl">
          "L'intelligence artificielle ne se délègue plus. <span className="text-[#D4AF37] italic">Elle se gouverne.</span>"
        </p>
        <p className="mt-10 text-lg text-white/70 max-w-3xl leading-relaxed">
          Former les leaders. Outiller les métiers. Transformer l'Afrique.
        </p>
      </div>
    </section>

    {/* PROGRAMMES TEASER */}
    <section className="py-24 lg:py-32 bg-[#FAFAFA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeader
          number="— 02"
          eyebrow="Programmes"
          title="Trois moments stratégiques 2026–2027."
          subtitle="Pilotage. Commandement. Métiers augmentés."
          body="Une cohorte par discipline exécutive — à Johannesburg et Cape Town."
          titleFont="eisg"
        />
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {PROGRAMMES.map((p) => (
            <article key={p.id} className="bg-white border border-[#0A192F]/10 lift-card overflow-hidden flex flex-col group">
              <div className="relative h-56 overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-4 left-4 bg-[#0A192F] text-white px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-semibold">
                  {p.location}
                </div>
                <div className="absolute top-4 right-4 font-eisg text-4xl text-white drop-shadow-lg">{p.number}</div>
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <div className="overline text-[#F58220]">{p.short}</div>
                <h3 className="mt-3 font-eisg text-xl text-[#0A192F] tracking-tight leading-tight flex-1">{p.title}</h3>
                <div className="mt-5 flex items-center justify-between border-t border-[#0A192F]/10 pt-5">
                  <div>
                    <div className="text-[11px] text-[#0A192F]/60 uppercase tracking-wider">Investissement</div>
                    <div className="font-bd text-2xl text-[#0A192F] mt-1">{p.price}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-[#0A192F]/60 uppercase tracking-wider">Durée</div>
                    <div className="font-bd text-sm text-[#0A192F] mt-1">{p.duration}</div>
                  </div>
                </div>
                <Link to="/eisg/programmes" className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#F58220]">
                  Détails du programme <ArrowRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#0A192F] text-white border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
        <h3 className="font-eisg text-3xl sm:text-4xl tracking-tight max-w-2xl">
          Réservez votre place pour la cohorte 2026.
        </h3>
        <div className="flex flex-wrap gap-4">
          <Link to="/eisg/registration" className="btn-premium btn-premium-gold">Pré-inscription <ArrowRight size={16} /></Link>
          <Link to="/eisg/contact" className="btn-premium btn-premium-ghost">Contact</Link>
        </div>
      </div>
    </section>
  </div>
);

export default EISGHome;
